import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import {
  fetchAvgByAssessment,
  fetchAvgRating,
  fetchConversationPerDay,
  fetchOperatorStats,
  fetchQualityDetails,
  fetchQualitySummary,
  fetchQualityHeatmap,
  fetchQualityConsultantReport,
} from './api';
import {
  QualityDetailsParams,
  QualitySummaryParams,
  QualityHeatmapParams,
  QualityConsultantReportParams,
} from './types';

export const dashboardKeys = {
  all: [API_TAGS.DASHBOARDS] as const,
  avgByAssessment: (params?: { limit?: number; offset?: number }) =>
    [...dashboardKeys.all, 'avgByAssessment', { ...params }] as const,
  avgRating: (params?: { 'date-from'?: string | null; 'date-to'?: string | null }) =>
    [...dashboardKeys.all, 'avgRating', { ...params }] as const,
  conversationPerDay: (params?: { 'date-from'?: string | null; 'date-to'?: string | null }) =>
    [...dashboardKeys.all, 'conversationPerDay', { ...params }] as const,
  operator: (params?: { limit?: number; offset?: number }) =>
    [...dashboardKeys.all, 'operator', { ...params }] as const,
};

export const useAvgByAssessment = (params?: { limit?: number; offset?: number }) => {
  return useQuery({
    queryKey: dashboardKeys.avgByAssessment(params),
    queryFn: () => fetchAvgByAssessment(params),
  });
};

export const useAvgRating = (params?: { 'date-from'?: string | null; 'date-to'?: string | null }) => {
  return useQuery({
    queryKey: dashboardKeys.avgRating(params),
    queryFn: () => fetchAvgRating(params),
  });
};

export const useConversationPerDay = (params?: { 'date-from'?: string | null; 'date-to'?: string | null }) => {
  return useQuery({
    queryKey: dashboardKeys.conversationPerDay(params),
    queryFn: () => fetchConversationPerDay(params),
  });
};

export const useOperatorStats = (params?: { limit?: number; offset?: number }) => {
  return useQuery({
    queryKey: dashboardKeys.operator(params),
    queryFn: () => fetchOperatorStats(params),
  });
};

export const useQualityDetails = (params?: QualityDetailsParams, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [...dashboardKeys.all, 'qualityDetails', { ...params }] as const,
    queryFn: () => fetchQualityDetails(params),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
};

export const useQualitySummary = (params?: QualitySummaryParams) => {
  return useQuery({
    queryKey: [...dashboardKeys.all, 'qualitySummary', { ...params }] as const,
    queryFn: () => fetchQualitySummary(params),
    placeholderData: keepPreviousData,
  });
};

export const useQualityHeatmap = (params?: QualityHeatmapParams) => {
  return useQuery({
    queryKey: [...dashboardKeys.all, 'qualityHeatmap', { ...params }] as const,
    queryFn: () => fetchQualityHeatmap(params),
    placeholderData: keepPreviousData,
  });
};

export const useQualityConsultantReport = (params: QualityConsultantReportParams | null) => {
  return useQuery({
    queryKey: [...dashboardKeys.all, 'qualityConsultantReport', { ...params }] as const,
    queryFn: () => fetchQualityConsultantReport(params!),
    enabled: !!params,
    placeholderData: keepPreviousData,
  });
};
